import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { getCaseStudies } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ArrowRight, Download, Building2, Target, CheckCircle2,
  TrendingUp, BarChart3, Search, Filter, X,
  Grid3X3, List, ArrowLeft, Loader2
} from "lucide-react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CaseStudiesPage() {
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIndustry, setSelectedIndustry] = useState("all");
  const [viewMode, setViewMode] = useState("grid");
  const [selectedStudy, setSelectedStudy] = useState(null);

  useEffect(() => {
    const fetchStudies = async () => {
      try {
        const res = await getCaseStudies();
        setStudies(res.data);
      } catch (error) {
        toast.error("Failed to load case studies");
      } finally {
        setLoading(false);
      }
    };
    fetchStudies();
  }, []);

  const getDownloadUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    return `${API_URL}${url}`;
  };

  const industries = [...new Set(studies.map(s => s.industry).filter(Boolean))];

  const filteredStudies = studies.filter(s => {
    const matchesSearch = searchQuery === "" ||
      s.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.industry?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.challenge?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesIndustry = selectedIndustry === "all" || s.industry === selectedIndustry;
    return matchesSearch && matchesIndustry;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-20 md:pt-24 flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-[#0066CC]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="case-studies-page">
      <Navbar />

      <main className="pt-16 md:pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-10 md:py-16 relative overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'40\' height=\'40\' viewBox=\'0 0 40 40\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.3\'%3E%3Cpath d=\'M20 20h20v20H20V20zM0 0h20v20H0V0z\'/%3E%3C/g%3E%3C/svg%3E")' }} />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link
              to="/"
              className="inline-flex items-center text-white/80 hover:text-white text-sm mb-6 transition-colors"
              data-testid="back-to-home-link"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Link>

            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center">
                <BarChart3 className="h-6 w-6 md:h-7 md:w-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-white">
                  Case <span className="text-[#4d9dff]">Studies</span>
                </h1>
                <p className="text-white/70 text-sm md:text-lg mt-1">
                  Real challenges, real implementations, real results
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 mt-4">
              <Badge variant="secondary" className="bg-white/20 text-white border-white/30">
                {studies.length} Case Stud{studies.length !== 1 ? "ies" : "y"}
              </Badge>
              <Badge variant="secondary" className="bg-white/20 text-white border-white/30">
                {industries.length} Industr{industries.length !== 1 ? "ies" : "y"}
              </Badge>
            </div>
          </div>
        </section>

        {/* Filters Section */}
        <section className="bg-white border-b border-slate-200 sticky top-16 md:top-20 z-30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 md:py-4">
            <div className="flex flex-col md:flex-row gap-3 md:gap-4 items-start md:items-center justify-between">
              <div className="relative w-full md:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search by title, client, or industry..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="case-study-search-input"
                />
              </div>

              <div className="flex items-center gap-3 w-full md:w-auto">
                <div className="flex items-center gap-2 flex-1 md:flex-none">
                  <Filter className="h-4 w-4 text-slate-500 hidden sm:block" />
                  <select
                    value={selectedIndustry}
                    onChange={(e) => setSelectedIndustry(e.target.value)}
                    className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-[#0066CC]/20 focus:border-[#0066CC] w-full md:w-auto"
                    data-testid="industry-filter"
                  >
                    <option value="all">All Industries</option>
                    {industries.map((ind) => (
                      <option key={ind} value={ind}>{ind}</option>
                    ))}
                  </select>
                </div>

                <div className="flex border border-slate-300 rounded-lg overflow-hidden flex-shrink-0">
                  <button
                    onClick={() => setViewMode("grid")}
                    className={`p-2 ${viewMode === "grid" ? "bg-[#0066CC] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                    data-testid="grid-view-btn"
                  >
                    <Grid3X3 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setViewMode("list")}
                    className={`p-2 ${viewMode === "list" ? "bg-[#0066CC] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                    data-testid="list-view-btn"
                  >
                    <List className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Case Studies Grid/List */}
        <section className="py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {filteredStudies.length === 0 ? (
              <div className="text-center py-16">
                <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-700 mb-2">
                  {searchQuery || selectedIndustry !== "all" ? "No matching case studies" : "Case studies coming soon"}
                </h3>
                <p className="text-slate-500 max-w-md mx-auto">
                  {searchQuery || selectedIndustry !== "all"
                    ? "Try adjusting your search or filter criteria"
                    : "We're preparing detailed case studies from our client implementations."
                  }
                </p>
                {(searchQuery || selectedIndustry !== "all") && (
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => { setSearchQuery(""); setSelectedIndustry("all"); }}
                    data-testid="clear-filters-btn"
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            ) : viewMode === "grid" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6" data-testid="case-studies-grid">
                {filteredStudies.map((study) => (
                  <CaseStudyCard
                    key={study.id}
                    study={study}
                    getDownloadUrl={getDownloadUrl}
                    onViewDetails={() => setSelectedStudy(study)}
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-4" data-testid="case-studies-list">
                {filteredStudies.map((study) => (
                  <CaseStudyListItem
                    key={study.id}
                    study={study}
                    getDownloadUrl={getDownloadUrl}
                    onViewDetails={() => setSelectedStudy(study)}
                  />
                ))}
              </div>
            )}

            {filteredStudies.length > 0 && (
              <p className="text-center text-slate-500 text-sm mt-8">
                Showing {filteredStudies.length} of {studies.length} case stud{studies.length !== 1 ? "ies" : "y"}
              </p>
            )}
          </div>
        </section>
      </main>

      {/* Detail Modal */}
      <Dialog open={!!selectedStudy} onOpenChange={() => setSelectedStudy(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="case-study-detail-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900 pr-6">
              {selectedStudy?.title}
            </DialogTitle>
          </DialogHeader>
          {selectedStudy && (
            <div className="space-y-5 mt-2">
              <div className="flex flex-wrap items-center gap-2">
                <Badge className="bg-[#0066CC] text-white">
                  <Building2 className="w-3 h-3 mr-1" />
                  {selectedStudy.industry}
                </Badge>
                <span className="text-sm text-slate-500">Client: <strong>{selectedStudy.client_name}</strong></span>
              </div>

              {selectedStudy.metrics && selectedStudy.metrics.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {selectedStudy.metrics.map((metric, idx) => (
                    <div key={idx} className="bg-slate-50 rounded-lg p-3 text-center">
                      <p className="text-lg font-bold text-[#0066CC]">{metric.value}</p>
                      <p className="text-xs text-slate-500">{metric.label}</p>
                    </div>
                  ))}
                </div>
              )}

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Target className="w-4 h-4 text-red-500" />
                  <h3 className="font-semibold text-slate-900 text-sm">The Challenge</h3>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed pl-6">{selectedStudy.challenge}</p>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  <h3 className="font-semibold text-slate-900 text-sm">Our Solution</h3>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed pl-6">{selectedStudy.solution}</p>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-[#0066CC]" />
                  <h3 className="font-semibold text-slate-900 text-sm">Results & Impact</h3>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed pl-6">{selectedStudy.results}</p>
              </div>

              {getDownloadUrl(selectedStudy.download_url) && (
                <a
                  href={getDownloadUrl(selectedStudy.download_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0066CC] text-white font-semibold text-sm hover:bg-[#0052A3] transition-colors"
                  data-testid="modal-download-pdf"
                >
                  <Download className="w-4 h-4" />
                  Download Full Case Study (PDF)
                </a>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}

function CaseStudyCard({ study, getDownloadUrl, onViewDetails }) {
  const dlUrl = getDownloadUrl(study.download_url);
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className="group bg-white rounded-xl overflow-hidden shadow-sm border border-slate-200 hover:shadow-lg hover:border-[#0066CC]/30 transition-all duration-300 flex flex-col"
      data-testid={`case-study-card-${study.id}`}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0066CC] to-[#0052A3] px-4 py-4">
        <Badge className="bg-white/20 text-white border-white/30 text-xs mb-2">
          <Building2 className="w-3 h-3 mr-1" />
          {study.industry}
        </Badge>
        <h3 className="text-base font-bold text-white line-clamp-2 leading-tight">
          {study.title}
        </h3>
        <p className="text-blue-100 text-xs mt-1">Client: {study.client_name}</p>
      </div>

      {/* KPI chips */}
      {study.metrics && study.metrics.length > 0 && (
        <div className="flex flex-wrap gap-2 px-4 pt-3">
          {study.metrics.slice(0, 3).map((m, i) => (
            <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-50 text-[#0066CC] text-xs font-medium">
              {m.value}
            </span>
          ))}
          {study.metrics.length > 3 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-xs">
              +{study.metrics.length - 3} more
            </span>
          )}
        </div>
      )}

      {/* Challenge preview */}
      <div className="px-4 py-3 flex-1">
        <div className="flex items-center gap-1.5 mb-1.5">
          <Target className="w-3.5 h-3.5 text-red-500" />
          <span className="text-xs font-semibold text-slate-700">Challenge</span>
        </div>
        <p className="text-slate-600 text-xs leading-relaxed line-clamp-3">
          {study.challenge}
        </p>
      </div>

      {/* Footer */}
      <div className="px-4 pb-4 pt-1 flex items-center gap-2 mt-auto">
        <Button
          size="sm"
          onClick={onViewDetails}
          className="bg-[#0066CC] hover:bg-[#0052A3] rounded-full text-xs flex-1"
          data-testid={`case-study-view-${study.id}`}
        >
          View Details
          <ArrowRight className="w-3.5 h-3.5 ml-1" />
        </Button>
        {dlUrl && (
          <a
            href={dlUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-slate-300 hover:border-[#0066CC] hover:text-[#0066CC] text-slate-500 transition-colors"
            data-testid={`case-study-download-${study.id}`}
          >
            <Download className="w-3.5 h-3.5" />
          </a>
        )}
      </div>
    </motion.div>
  );
}

function CaseStudyListItem({ study, getDownloadUrl, onViewDetails }) {
  const dlUrl = getDownloadUrl(study.download_url);
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="group bg-white rounded-xl overflow-hidden shadow-sm border border-slate-200 hover:shadow-lg hover:border-[#0066CC]/30 transition-all duration-300 flex flex-col md:flex-row"
      data-testid={`case-study-list-item-${study.id}`}
    >
      {/* Left: industry color bar + info */}
      <div className="bg-gradient-to-b md:bg-gradient-to-r from-[#0066CC] to-[#0052A3] p-4 md:w-64 md:flex-shrink-0 flex flex-row md:flex-col gap-3 md:gap-0 md:justify-center">
        <Badge className="bg-white/20 text-white border-white/30 text-xs w-fit mb-0 md:mb-2">
          <Building2 className="w-3 h-3 mr-1" />
          {study.industry}
        </Badge>
        <div className="hidden md:block">
          <p className="text-blue-100 text-xs mt-1">Client</p>
          <p className="text-white font-semibold text-sm">{study.client_name}</p>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 md:p-5 flex-1 flex flex-col justify-center">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-slate-900 text-base md:text-lg line-clamp-1">{study.title}</h3>
            <p className="text-sm text-slate-500 md:hidden">Client: {study.client_name}</p>
          </div>
          {study.metrics && study.metrics.length > 0 && (
            <div className="hidden sm:flex gap-2 flex-shrink-0">
              {study.metrics.slice(0, 2).map((m, i) => (
                <span key={i} className="inline-flex items-center px-2.5 py-1 rounded-full bg-blue-50 text-[#0066CC] text-xs font-semibold">
                  {m.value}
                </span>
              ))}
            </div>
          )}
        </div>

        <p className="text-slate-600 text-sm leading-relaxed line-clamp-2 mt-2">{study.challenge}</p>

        <div className="flex items-center gap-3 mt-3">
          <Button
            size="sm"
            onClick={onViewDetails}
            className="bg-[#0066CC] hover:bg-[#0052A3] rounded-full text-xs"
            data-testid={`case-study-list-view-${study.id}`}
          >
            View Details
            <ArrowRight className="w-3.5 h-3.5 ml-1" />
          </Button>
          {dlUrl && (
            <a
              href={dlUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-slate-600 hover:text-[#0066CC] transition-colors flex items-center gap-1"
            >
              <Download className="w-3.5 h-3.5" />
              PDF
            </a>
          )}
        </div>
      </div>
    </motion.div>
  );
}
